import { GetDistance } from './GeoFunctions';
import {
  encodeGeohash,
  geohashNeighbors,
  GEOHASH_PRECISION,
  MIN_NEARBY_DRIVERS,
} from './geohash';
import { isFreshLocation } from './locationThrottle';
import { resolveDriverRadius } from './dispatchWaves.js';

/** /locations guarda muchos conductores desconectados: se revisan los más cercanos hasta este tope. */
export const MAX_PUBLIC_LOOKUPS = 150;
/** Celdas de ~1,2 km: en zonas densas basta con ellas y no se bajan cientos de ubicaciones. */
export const DENSE_GEOHASH_PRECISION = GEOHASH_PRECISION + 1;
/** Elegibles necesarios para quedarse en las celdas finas (lista del mapa y candidatos del despacho). */
export const DENSE_MIN_DRIVERS = 20;
export const DENSE_MIN_DRIVERS_RANGE = { min: MIN_NEARBY_DRIVERS, max: 100 };

/** /settings/denseMinDrivers (panel web, Configuración > Operación); fuera de rango usa DENSE_MIN_DRIVERS. */
export const denseMinDrivers = (settings = {}) => {
  const value = Number(settings.denseMinDrivers);
  return Number.isInteger(value) && value >= DENSE_MIN_DRIVERS_RANGE.min && value <= DENSE_MIN_DRIVERS_RANGE.max
    ? value
    : DENSE_MIN_DRIVERS;
};
export const PUBLIC_CACHE_TTL_MS = 10 * 60 * 1000;

const locationKey = (loc) => (loc ? `${loc.lat},${loc.lng}` : '');

/**
 * Caché de drivers_public por conductor.
 * - Activos: se reutilizan durante `ttlMs`; `queue` y `active` se vuelven a leer al pedir el viaje.
 * - Inactivos o inexistentes: se releen en cuanto cambia su ubicación (el conductor se conectó).
 */
export function createPublicCache({ ttlMs = PUBLIC_CACHE_TTL_MS, read, now = () => Date.now() }) {
  const entries = new Map();
  const fetchFresh = async (uids, locations, result) => {
    await Promise.all(uids.map(async (uid) => {
      let data = null;
      try {
        data = (await read(uid)) || null;
      } catch (_) {
        return;
      }
      entries.set(uid, { data, ts: now(), locKey: locationKey(locations?.[uid]) });
      result[uid] = data;
    }));
  };
  return {
    /** Devuelve { uid: pub | null } y cuántas lecturas hizo a la base de datos. */
    async get(uids, locations = {}) {
      const result = {};
      const missing = uids.filter((uid) => {
        const hit = entries.get(uid);
        const fresh = hit && now() - hit.ts < ttlMs
          && (hit.data?.active === true || hit.locKey === locationKey(locations[uid]));
        if (fresh) result[uid] = hit.data;
        return !fresh;
      });
      await fetchFresh(missing, locations, result);
      return { publics: result, reads: missing.length };
    },
    /** Lectura sin caché (disponibilidad al pedir el viaje); actualiza la caché. Sin clave si la lectura falló. */
    async refresh(uids) {
      const result = {};
      await fetchFresh(uids, {}, result);
      return result;
    },
    clear() {
      entries.clear();
    },
  };
}

export const isValidLocation = (loc) => !!loc
  && loc.lat != null && loc.lng != null
  && !isNaN(Number(loc.lat)) && !isNaN(Number(loc.lng));

export const isDriverEligible = (pub, settings = {}) => {
  // Alineado con isDispatchEligible del servidor: en cola / sin push no deben aparecer ni pedirse.
  if (!pub || pub.active !== true || pub.queue === true) return false;
  if (pub.hasPushToken !== true) return false;
  const hasLicense = pub.hasLicense || !settings.license_image_required;
  const carApproved = pub.carApproved || !settings.carType_required;
  const hasTerm = pub.term || !settings.term_required;
  return !!(hasLicense && carApproved && hasTerm);
};

export const toDriverEntry = (uid, pub, location, distance) => ({
  id: uid,
  uid,
  location,
  distance,
  carType: pub.carType || null,
  vehicleNumber: pub.vehicleNumber || null,
  fleetadmin: pub.fleetadmin || null,
  gender: pub.gender || null,
  firstName: pub.firstName || '',
  lastName: pub.lastName || '',
  queue: pub.queue === true,
});

/** Radio de búsqueda del mapa / semilla de pedido: respeta settings.driverRadius (sin piso forzado a 15). */
export const lookupRadiusKm = (settings = {}) => {
  const radius = resolveDriverRadius(settings);
  return settings.convert_to_mile ? radius * 1.609344 : radius;
};

/**
 * Conductores elegibles cerca de `center` sin descargar /users:
 *  - settings.driversGeoIndexOnly === true: celdas geohash sobre /locations, de la más fina a la
 *    más amplia: precisión 6 si alcanza denseMinDrivers(settings) elegibles, si no 5 (MIN_NEARBY_DRIVERS) y 4.
 *  - en otro caso, /locations completo: las versiones de la app que no guardan geohash
 *    solo se encuentran así. Activar driversGeoIndexOnly cuando casi todos los conductores
 *    activos tengan la versión nueva.
 * Luego se lee el perfil público (drivers_public/{uid}) de los más cercanos.
 *
 * queryPrefix(prefix) -> { uid: loc } | null
 * getAllLocations() -> { uid: loc } | null
 * getDriverPublics(uids, locations) -> { uid: pub | null }
 * now: hora del servidor, para descartar ubicaciones con `ts` viejo.
 */
export async function findNearbyDrivers({
  center,
  settings = {},
  maxDrivers = 40,
  queryPrefix,
  getAllLocations,
  getDriverPublics,
  now = Date.now(),
}) {
  const locations = {};
  const publics = {};
  const geoIndexOnly = settings.driversGeoIndexOnly === true;
  const stats = { source: geoIndexOnly ? 'geohash' : 'locations', prefixQueries: 0, publicLookups: 0 };
  const radiusKm = lookupRadiusKm(settings);
  const distanceTo = (loc) => GetDistance(center.lat, center.lng, Number(loc.lat), Number(loc.lng));

  const addLocations = (found) => {
    Object.keys(found || {}).forEach((uid) => {
      if (isValidLocation(found[uid]) && isFreshLocation(found[uid], now)) locations[uid] = found[uid];
    });
  };

  const scan = async (precision) => {
    const cells = geohashNeighbors(encodeGeohash(center.lat, center.lng, precision));
    stats.prefixQueries += cells.length;
    const results = await Promise.all(cells.map((cell) => Promise.resolve()
      .then(() => queryPrefix(cell))
      .catch(() => null)));
    results.forEach(addLocations);
  };

  const eligibleIds = () => Object.keys(publics).filter((uid) => isDriverEligible(publics[uid], settings));

  /** Perfiles de los más cercanos, por lotes, hasta completar `maxDrivers` elegibles o el tope de lecturas. */
  const resolvePublics = async () => {
    const candidates = Object.keys(locations)
      .filter((uid) => !(uid in publics))
      .map((uid) => ({ uid, distance: distanceTo(locations[uid]) }))
      .filter((item) => item.distance <= radiusKm)
      .sort((a, b) => a.distance - b.distance)
      .map((item) => item.uid);
    const batchSize = Math.max(MIN_NEARBY_DRIVERS, maxDrivers);
    while (candidates.length && eligibleIds().length < maxDrivers) {
      const budget = MAX_PUBLIC_LOOKUPS - Object.keys(publics).length;
      if (budget <= 0) return;
      const ids = candidates.splice(0, Math.min(batchSize, budget));
      stats.publicLookups += ids.length;
      const subset = {};
      ids.forEach((uid) => { subset[uid] = locations[uid]; });
      // eslint-disable-next-line no-await-in-loop
      const found = (await getDriverPublics(ids, subset)) || {};
      ids.forEach((uid) => { publics[uid] = found[uid] || null; });
    }
  };

  if (geoIndexOnly) {
    const stages = [
      [DENSE_GEOHASH_PRECISION, Math.min(denseMinDrivers(settings), maxDrivers)],
      [GEOHASH_PRECISION, MIN_NEARBY_DRIVERS],
      [GEOHASH_PRECISION - 1, 0],
    ];
    for (const [precision, enough] of stages) {
      // eslint-disable-next-line no-await-in-loop
      await scan(precision);
      // eslint-disable-next-line no-await-in-loop
      await resolvePublics();
      if (eligibleIds().length >= enough) break;
    }
  } else {
    addLocations(await getAllLocations());
    await resolvePublics();
  }

  const drivers = eligibleIds()
    .map((uid) => toDriverEntry(uid, publics[uid], locations[uid], distanceTo(locations[uid])))
    .sort((a, b) => a.distance - b.distance)
    .slice(0, maxDrivers);
  return { drivers, stats };
}

/** Panel web: todos los conductores activos con ubicación (lectura indexada por `active`). */
export function buildActiveDriversList({ publics, locations, settings = {}, center = null, now = Date.now() }) {
  return Object.keys(publics || {})
    .filter((uid) => isValidLocation(locations?.[uid]) && isFreshLocation(locations[uid], now) && isDriverEligible(publics[uid], settings))
    .map((uid) => {
      const loc = locations[uid];
      const distance = center ? GetDistance(center.lat, center.lng, Number(loc.lat), Number(loc.lng)) : 0;
      return toDriverEntry(uid, publics[uid], loc, distance);
    })
    .sort((a, b) => a.distance - b.distance);
}
